import { Button } from "@moe/oss/ui/button";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Navbar } from "@web/components/Navbar";
import { useSettingsMutation } from "@web/hooks/use-settings-mutation";
import { sb } from "@web/lib/supabase";
import { useAppContext } from "@web/route-services/root/AppContext";
import { DeleteAccountModal } from "@web/route-services/settings/DeleteAccountModal";
import { Sidepane } from "@web/route-services/settings/Sidepane";
import { motion } from "motion/react";
import { useState } from "react";

export const Route = createFileRoute("/settings/account")({
  component: AccountPage
});
export const fontScaleQK = "fontScale";

function AccountPage() {
  const { settings, createModal } = useAppContext();
  const navigate = useNavigate();
  const { isDesktopScreen } = useAppContext();
  const settingsMutation = useSettingsMutation();

  // Default to cached font scale or 1 if not cached
  const [fontScale, setFontScale] = useState(() => {
    const cached = localStorage.getItem(fontScaleQK);
    if (cached) return parseFloat(cached);
    return 1;
  });

  const signOut = async () => {
    await sb.auth.signOut({});
    navigate({ to: "/" });
  };

  return (
    <div className="scroll-secondary from-background-gradient via-background to-background text-tx-secondary flex h-screen w-screen bg-gradient-to-b text-sm antialiased antialiased md:overflow-hidden">
      <Navbar />
      <div style={{ scrollbarGutter: "stable" }} className="scroll-home-page flex w-full flex-row overflow-auto py-4">
        {isDesktopScreen && <Sidepane />}
        <div className="flex h-full w-full flex-col items-center space-y-5 px-3 md:justify-center md:px-8">
          <SettingsCard>
            {settings && (
              <div className="flex flex-col space-y-6">
                <div className="flex items-center justify-between">
                  <label className="font-medium">Delete Account</label>
                  <Button
                    variant="destructive"
                    className="w-fit"
                    onClick={() => createModal({ content: <DeleteAccountModal /> })}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            )}
            <Footer signOut={signOut} />
          </SettingsCard>
        </div>
      </div>
    </div>
  );
}

function SettingsCard({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      className="text-tx-secondary mt-20 flex w-full max-w-md flex-col items-center md:mt-0 md:max-w-2xl"
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.2,
        ease: [0, 0.71, 0.2, 1.01]
      }}
    >
      <h1 className="text-tx-primary pb-4 text-2xl font-bold tracking-wide">Account Settings</h1>
      <div className="border-line bg-container-primary flex h-fit w-full flex-col space-y-6 rounded-2xl border px-8 pb-3 pt-6 md:h-[10rem] md:w-[30rem]">
        {children}
      </div>
    </motion.div>
  );
}

function Footer({ signOut }: { signOut: () => Promise<void> }) {
  return (
    <div className="flex w-full grow flex-col justify-end">
      <div className="flex w-full flex-row items-baseline justify-between">
        <Button variant="clear" onClick={signOut} className="px-0">
          Sign Out
        </Button>
        <Link to={"/settings/reset-password"} className="text-tx-tertiary font-medium">
          Reset Password
        </Link>
      </div>
    </div>
  );
}
